/**
 * Durable raw EEG + qEEG synchronized review layout helpers.
 */

export const QEEG_RAW_LAYOUT_SCHEMA_VERSION = "qeeg-raw-layout-v1.0.0";

export type QeegRawSplitOrientation = "horizontal" | "vertical";

export type QeegRawLinkedLayout = {
  schemaVersion: string;
  orientation: QeegRawSplitOrientation;
  /** Fraction of viewport allocated to raw EEG (0.2–0.8). */
  rawPaneRatio: number;
  trendPaneVisible: boolean;
  linkedSeekEnabled: boolean;
  syncWindowSeconds: number;
  lastSeekSeconds: number | null;
  durable: boolean;
};

export function createDefaultQeegRawLayout(
  overrides: Partial<QeegRawLinkedLayout> = {},
): QeegRawLinkedLayout {
  return {
    schemaVersion: QEEG_RAW_LAYOUT_SCHEMA_VERSION,
    orientation: "horizontal",
    rawPaneRatio: 0.62,
    trendPaneVisible: true,
    linkedSeekEnabled: true,
    syncWindowSeconds: 10,
    lastSeekSeconds: null,
    durable: true,
    ...overrides,
  };
}

export function setRawPaneRatio(
  layout: QeegRawLinkedLayout,
  ratio: number,
): QeegRawLinkedLayout {
  const clamped = Math.max(0.2, Math.min(0.8, ratio));
  return { ...layout, rawPaneRatio: clamped };
}

export function mapTrendClickToRawSeek(
  layout: QeegRawLinkedLayout,
  trendTimeSeconds: number,
): { seekSeconds: number; window: { start: number; end: number } } | null {
  if (!layout.linkedSeekEnabled) return null;
  const half = layout.syncWindowSeconds / 2;
  const seekSeconds = Math.max(0, trendTimeSeconds);
  return {
    seekSeconds,
    window: {
      start: Math.max(0, seekSeconds - half),
      end: seekSeconds + half,
    },
  };
}

export function persistLayoutKey(studyId: string, userId: string): string {
  return `qeeg-raw-layout:${studyId}:${userId}`;
}

export function readLocalReviewLayout(
  studyId: string,
  userId: string,
): QeegRawLinkedLayout | null {
  if (typeof localStorage === "undefined") return null;
  try {
    const raw = localStorage.getItem(persistLayoutKey(studyId, userId));
    if (!raw) return null;
    return createDefaultQeegRawLayout(JSON.parse(raw));
  } catch {
    return null;
  }
}

export function writeLocalReviewLayout(
  studyId: string,
  userId: string,
  layout: QeegRawLinkedLayout,
): void {
  if (typeof localStorage === "undefined") return;
  try {
    localStorage.setItem(
      persistLayoutKey(studyId, userId),
      JSON.stringify(layout),
    );
  } catch {
    /* ignore quota */
  }
}
